import DOMPurify from 'dompurify'
import { type FormsCustomTemplatesQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type FormCustomTemplate = FormsCustomTemplatesQuery['forms']['formsConnection']['nodes'][number]

const MAX_TEXT_CHARACTERS = 30

export type ColumnRenderer = (item: FormCustomTemplate) => {
  type: 'text' | 'dotTag' | 'status' | 'person'
  value: {
    text?: React.ReactNode
    label?: string
    color?: string
    status?: string
    src?: string
    firstName?: string
    lastName?: string
  }
}

export enum TableColumnType {
  Name = 'name',
  FormType = 'formType',
  Status = 'status',
  Author = 'author',
}

export type TableColumn = {
  label: string
  render: ColumnRenderer
  sorting?: string
}

//configuration needed for the tag column type
export type TagColumnConfig = {
  valueMapping?: Record<string, string>
  colorMapping?: Record<string, string>
}

//configuration needed for the status column type
export type StatusColumnConfig = {
  valueMapping?: Record<string, string>
  colorMapping?: Record<string, string>
}

// Unified column configuration
export type ColumnConfigurations = {
  [TableColumnType.FormType]?: TagColumnConfig
  [TableColumnType.Status]?: StatusColumnConfig
}

// Configurable Cell Renderers
const createNameCell: ColumnRenderer = (item: FormCustomTemplate) => {
  const title = item?.title ?? ''

  return {
    type: 'text' as const,
    value: {
      text: title.length > MAX_TEXT_CHARACTERS ? title.slice(0, MAX_TEXT_CHARACTERS) + '…' : title,
    },
  }
}

const createFormTypeCell =
  (config?: TagColumnConfig): ColumnRenderer =>
  (item: FormCustomTemplate) => {
    const type = item?.formType
    const displayText = config?.valueMapping?.[type] ?? ''
    const color = config?.colorMapping?.[type] ?? 'neutral' //default color
    return {
      type: 'dotTag' as const,
      value: {
        label: displayText,
        color: color,
      },
    }
  }

const createStatusCell =
  (config?: StatusColumnConfig): ColumnRenderer =>
  (item: FormCustomTemplate) => {
    const status = item?.status
    const displayText = config?.valueMapping?.[status] ?? ''
    const color = config?.colorMapping?.[status] ?? ''
    return {
      type: 'status' as const,
      value: {
        label: displayText,
        status: color,
      },
    }
  }

const createAuthorCell: ColumnRenderer = (item: FormCustomTemplate) => {
  return {
    type: 'person' as const,
    value: {
      src: buildUrl(item.author?.employee?.avatar?.url ?? ''),
      firstName: item.author?.employee?.firstName ?? '',
      lastName: item.author?.employee?.lastName ?? '',
    },
  }
}

// Column creation functions (similar to FormResponses pattern)
export const createBaseColumns = (
  columns: TableColumnType[],
  configurations?: ColumnConfigurations
): TableColumn[] => {
  const builtColumns: TableColumn[] = []

  if (columns.includes(TableColumnType.Name)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.name'),
      render: createNameCell,
    })
  }

  if (columns.includes(TableColumnType.FormType)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.type'),
      render: createFormTypeCell(configurations?.[TableColumnType.FormType]),
    })
  }

  if (columns.includes(TableColumnType.Status)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.status'),
      render: createStatusCell(configurations?.[TableColumnType.Status]),
    })
  }

  if (columns.includes(TableColumnType.Author)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.author'),
      render: createAuthorCell,
    })
  }

  return builtColumns
}

type ListItemDefinition = (item: FormCustomTemplate) => {
  title: string
  description: string[]
}

type ListField = {
  label: string
  render: (item: FormCustomTemplate) => {
    type: 'text' | 'dotTag' | 'status' | 'person' | 'tag'
    value: unknown
  }
}

type ListFields = {
  itemDefinition: ListItemDefinition
  fields: ListField[]
}
// Column creation functions (similar to FormResponses pattern)
export const createBaseColumnsList = (
  columns: TableColumnType[],
  configurations?: ColumnConfigurations
): ListFields => {
  const builtColumns: ListField[] = []
  const itemDefinition = (item: FormCustomTemplate) => {
    const nameCell = createNameCell(item)
    const title = typeof nameCell.value.text === 'string' ? nameCell.value.text : ''
    const description: string[] = item.description
      ? [DOMPurify.sanitize(item.description, { ALLOWED_TAGS: [] })]
      : []
    return { title, description }
  }

  if (columns.includes(TableColumnType.FormType)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.type'),
      render: createFormTypeCell(configurations?.[TableColumnType.FormType]),
    })
  }

  if (columns.includes(TableColumnType.Status)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.status'),
      render: createStatusCell(configurations?.[TableColumnType.Status]),
    })
  }

  if (columns.includes(TableColumnType.Author)) {
    builtColumns.push({
      label: i18n.t('forms.custom_templates.data_collection.author'),
      render: createAuthorCell,
    })
  }

  return {
    itemDefinition,
    fields: builtColumns,
  }
}
